import { CARD_HANDOVER_LOCATION_SORT_KEYS, type CardHandoverLocationListItem, type CardHandoverLocationSortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn } from "../../components/common/list-columns";
import { type HandoverLocationFormTarget, HandoverLocationFormModal } from "../../components/handover-locations/handover-location-form-modal";
import { ActiveBadge } from "../../components/sports/form-parts";
import { inUseAsMessage } from "../../lib/api/in-use";
import { deleteHandoverLocation, handoverLocationStatsOptions, handoverLocationsListOptions, invalidateHandoverLocations } from "../../lib/handover-locations/queries";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { useListPage } from "../../lib/list-page/use-list-page";

export const Route = createFileRoute("/_app/settings/handover-locations")({
	validateSearch: listSearch(CARD_HANDOVER_LOCATION_SORT_KEYS, { is_active: z.enum(["true", "false"]) }),
	component: HandoverLocationsPage,
});

interface HandoverLocationFilters extends Record<string, unknown> {
	search: string;
	isActive: "true" | "false" | "";
}
const EMPTY_FILTERS: HandoverLocationFilters = { search: "", isActive: "" };

function HandoverLocationsPage() {
	const { t } = useTranslation("handoverLocations");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const search = Route.useSearch();
	const { session } = Route.useRouteContext();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<HandoverLocationFormTarget | null>(null);

	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		listOptions: (q) => handoverLocationsListOptions({ ...q, is_active: search.is_active }),
		columnsKey: "handover-locations",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): HandoverLocationFilters => ({ search: s.search ?? "", isActive: s.is_active ?? "" }),
			toUrl: (f) => ({ search: f.search.trim() || undefined, is_active: f.isActive || undefined }),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	const stats = useQuery(handoverLocationStatsOptions());
	const total = page.list.data?.total;
	const sortValue = `${search.sort ?? "sort_order"}:${search.dir ?? "asc"}`;
	const sortOptions = [
		{ value: "sort_order:asc", label: t("sort.order") },
		{ value: "name:asc", label: tc("sort.nameAsc") },
		{ value: "name:desc", label: tc("sort.nameDesc") },
		{ value: "code:asc", label: t("sort.codeAsc") },
		{ value: "created_at:desc", label: tc("sort.newest") },
		{ value: "created_at:asc", label: tc("sort.oldest") },
	];
	const del = useConfirmDelete<CardHandoverLocationListItem>({
		remove: (r) => inUseAsMessage(deleteHandoverLocation(r.card_handover_location_id), t("delete.inUse")),
		invalidate: invalidateHandoverLocations,
		deleted: (r) => t("delete.deleted", { name: r.name }),
	});

	const columns: ColumnDef<CardHandoverLocationListItem, CardHandoverLocationSortKey>[] = [
		{
			key: "name",
			header: t("table.location"),
			sortable: true,
			lockVisible: true,
			minWidth: "18rem",
			// Her entry-points row: the name bold, "code · address" under it.
			cell: (r) => {
				const sub = r.code && r.address ? t("table.codeAndAddress", { code: r.code, address: r.address }) : (r.code ?? r.address ?? undefined);
				return (
					<PrimaryCell sub={sub} title={sub ? `${r.name}\n${sub}` : r.name}>
						{r.name}
					</PrimaryCell>
				);
			},
		},
		{
			key: "is_active" as CardHandoverLocationSortKey,
			header: t("table.status"),
			minWidth: "6rem",
			cell: (r) => <ActiveBadge active={r.is_active} activeLabel={t("table.active")} inactiveLabel={t("table.inactive")} />,
		},
		{
			key: "sort_order",
			header: t("table.order"),
			sortable: true,
			width: "5rem",
			numeric: true,
			cell: (r) => <span className="tabular-nums text-gms-ink-3">{formatNumber(r.sort_order)}</span>,
		},
		timestampColumn("created_at", t("table.created"), true),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={t("page.intro")}
				actionLabel={t("page.add")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{ key: "total", label: t("stats.total"), value: stats.data?.total ?? "—" },
					...(stats.data
						? [
								{ key: "active", label: t("stats.active"), value: stats.data.active },
								{ key: "inactive", label: t("stats.inactive"), value: stats.data.inactive },
							]
						: []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				sort={{
					value: sortValue,
					options: sortOptions,
					onChange: (v) => {
						const [sort, dir] = v.split(":") as [CardHandoverLocationSortKey, "asc" | "desc"];
						page.patch({ sort, dir });
					},
				}}
				chips={[
					{
						kind: "options",
						key: "status",
						label: t("filters.statusChips"),
						options: [
							{ value: "true", label: t("table.active"), count: stats.data?.active },
							{ value: "false", label: t("table.inactive"), count: stats.data?.inactive },
						],
						value: search.is_active ? [search.is_active] : [],
						onChange: (v) => page.patch({ is_active: (v[0] as "true" | "false" | undefined) ?? undefined }),
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<CardHandoverLocationListItem, CardHandoverLocationSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.card_handover_location_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={
					isSuper
						? (r) => (
								<EditDeleteActions
									editLabel={t("table.editLabel", { name: r.name })}
									onEdit={() => setForm({ mode: "edit", location: r })}
									deleteLabel={t("table.deleteLabel", { name: r.name })}
									onDelete={() => del.ask(r)}
									variant="list"
								/>
							)
						: undefined
				}
			/>

			{isSuper && <HandoverLocationFormModal target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: del.row?.name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
